/**
 * WorkspaceDrawer — Workforce Hub W4 right-edge slide-in primitive.
 *
 * Replaces center-screen modals for multi-field edits and uploads so
 * operators stay inside the workspace context instead of being torn
 * out by a full-screen overlay.
 *
 * Anatomy:
 *
 *   ┌─ Backdrop (dimmed, click closes) ──────────────┐
 *   │                              ┌─ Drawer (480px) │
 *   │                              │ Header          │
 *   │                              │  ▸ Eyebrow      │
 *   │                              │  ▸ Title        │
 *   │                              │  ▸ Subtitle     │
 *   │                              │  ▸ Close (×)    │
 *   │                              ├─────────────────┤
 *   │                              │ Body            │
 *   │                              │ (children)      │
 *   │                              ├─────────────────┤
 *   │                              │ Actions footer  │
 *   │                              └─────────────────┘
 *   └────────────────────────────────────────────────┘
 *
 * Singleton enforcement
 *   A module-level open registry tracks every currently-open drawer.
 *   When a new drawer opens while another is already open, the older
 *   one is closed via its onClose callback. This is defense in depth
 *   — call sites are still expected to control the "open" prop so
 *   they don't try to render two at once.
 *
 * Accessibility
 *   - Tab / Shift+Tab focus trap (matches the Modal primitive)
 *   - Escape closes
 *   - Focus restores to the trigger element (the element that was
 *     focused when the drawer opened) on close
 *   - role="dialog", aria-modal="true", aria-labelledby + aria-describedby
 *   - Close button has a fixed label, focus moves into the body on open
 *
 * Telemetry
 *   The primitive is intentionally telemetry-free — the host page
 *   emits drawer.open / drawer.close / edit.commit / edit.abandon
 *   etc. so subtypes stay tied to the calling business workflow,
 *   not to a generic "drawer opened".
 */

import React, { useCallback, useEffect, useRef } from 'react';
import './WorkspaceDrawer.css';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'area[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'iframe',
  'object',
  'embed',
  '[contenteditable="true"]',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function getFocusable(container) {
  if (!container) return [];
  const nodes = container.querySelectorAll(FOCUSABLE_SELECTOR);
  return Array.from(nodes).filter(
    (el) => !el.hasAttribute('disabled') && el.getAttribute('aria-hidden') !== 'true'
  );
}

// Singleton registry — module scoped. Each open drawer registers a
// `requestClose` handler; opening a new drawer fires every previous
// one's handler before adding its own.
const openDrawerRegistry = new Set();

function registerDrawer(requestClose) {
  for (const previous of openDrawerRegistry) {
    if (previous !== requestClose) previous();
  }
  openDrawerRegistry.clear();
  openDrawerRegistry.add(requestClose);
  return () => { openDrawerRegistry.delete(requestClose); };
}

export function __resetWorkspaceDrawerRegistryForTests() {
  openDrawerRegistry.clear();
}

function WorkspaceDrawer({
  open,
  onClose,
  title,
  subtitle = null,
  eyebrow = null,
  actions = null,
  busy = false,
  width = 'md',
  className = '',
  children,
  initialFocusRef = null,
  'data-testid': testId = 'workspace-drawer'
}) {
  const drawerRef = useRef(null);
  const closeBtnRef = useRef(null);
  const prevFocusRef = useRef(null);

  const handleKeyDown = useCallback((event) => {
    if (event.key !== 'Tab') return;
    const focusables = getFocusable(drawerRef.current);
    if (focusables.length === 0) {
      event.preventDefault();
      return;
    }
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement;
    if (event.shiftKey) {
      if (active === first || !drawerRef.current.contains(active)) {
        event.preventDefault();
        last.focus();
      }
    } else if (active === last) {
      event.preventDefault();
      first.focus();
    }
  }, []);

  useEffect(() => {
    if (!open) return undefined;

    // Snapshot the trigger element so we can restore focus on close.
    prevFocusRef.current = document.activeElement;

    // Register with the singleton — this auto-closes any other drawer.
    const unregister = registerDrawer(() => onClose?.());

    const focusTimer = window.setTimeout(() => {
      const target =
        initialFocusRef?.current ||
        getFocusable(drawerRef.current).find((el) => el !== closeBtnRef.current) ||
        closeBtnRef.current;
      target?.focus();
    }, 0);

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose?.();
      }
    };
    window.addEventListener('keydown', handleEscape);

    return () => {
      window.clearTimeout(focusTimer);
      window.removeEventListener('keydown', handleEscape);
      unregister();
      // Restore focus to the trigger. setTimeout(0) so React unmounts
      // the drawer DOM first (otherwise focus() targets a node that's
      // about to be removed).
      const prev = prevFocusRef.current;
      if (prev && typeof prev.focus === 'function') {
        window.setTimeout(() => {
          try { prev.focus(); } catch { /* node may be unmounted */ }
        }, 0);
      }
    };
  }, [open, onClose, initialFocusRef]);

  if (!open) return null;

  return (
    <div
      className="workspace-drawer-backdrop"
      data-testid={`${testId}-backdrop`}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose?.();
      }}
    >
      <aside
        ref={drawerRef}
        className={`workspace-drawer workspace-drawer--${width}${className ? ` ${className}` : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? `${testId}-title` : undefined}
        aria-describedby={subtitle ? `${testId}-subtitle` : undefined}
        aria-busy={busy ? 'true' : undefined}
        data-testid={testId}
        onMouseDown={(e) => e.stopPropagation()}
        onKeyDown={handleKeyDown}
      >
        <header className="workspace-drawer__header">
          <div className="workspace-drawer__header-text">
            {eyebrow ? (
              <span className="workspace-drawer__eyebrow" data-testid={`${testId}-eyebrow`}>
                {eyebrow}
              </span>
            ) : null}
            {title ? (
              <h2 id={`${testId}-title`} className="workspace-drawer__title">
                {title}
              </h2>
            ) : null}
            {subtitle ? (
              <p id={`${testId}-subtitle`} className="workspace-drawer__subtitle">
                {subtitle}
              </p>
            ) : null}
          </div>
          <button
            ref={closeBtnRef}
            type="button"
            className="workspace-drawer__close"
            aria-label="Close"
            data-testid={`${testId}-close`}
            onClick={() => onClose?.()}
            disabled={busy}
          >
            ✕
          </button>
        </header>
        <div className="workspace-drawer__body">{children}</div>
        {actions ? (
          <footer className="workspace-drawer__footer">{actions}</footer>
        ) : null}
      </aside>
    </div>
  );
}

export default WorkspaceDrawer;
